import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { getObjectsWithParams, addObject } from "../../../../api/Api";
import { getReceiptHeaderInfo } from "../../../../utils/hospitalInfo";

export default function CollectEmergencyFees() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";

  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchVisits();
  }, []);

  const fetchVisits = async () => {
    try {
      setLoading(true);
      const response = await getObjectsWithParams(
        "emergency-visits",
        { payment_status: 1 }, // Only pending payments
        token
      );
      setVisits(response?.results || response || []);
    } catch (error) {
      console.error("Error fetching emergency visits:", error);
      setSnackbar({
        open: true,
        message: "Error fetching visits",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedVisit) return;

    try {
      await addObject(
        `emergency-visits/${selectedVisit.id}/mark-paid`,
        {
          payment_method: paymentMethod,
          payment_notes: paymentNotes,
        },
        token
      );

      setSnackbar({
        open: true,
        message: "Data added successfully!",
        severity: "success",
      });
      setPaymentDialog(false);
      setSelectedVisit(null);
      fetchVisits(); // Refresh the list
    } catch (error) {
      console.error("Error recording payment:", error);
      setSnackbar({
        open: true,
        message: "Failed to add data!",
        severity: "error",
      });
    }
  };

  const printReceipt = (visit) => {
    const { hospitalName, hospitalAddress, hospitalPhone, phoneLabel } =
      getReceiptHeaderInfo(t);

    const styles = `
      <style>
        @page { size: 80mm 200mm; margin: 0; }
        body { 
          font-family: "Courier New", monospace; 
          font-size: 12px;
          line-height: 1.2;
          color: #000;
          margin: 0;
          padding: 8px;
          background: white;
        }
        .receipt { width: 100%; max-width: 80mm; }
        .center { text-align: center; }
        .left { text-align: left; }
        .right { text-align: right; }
        .bold { font-weight: bold; }
        .large { font-size: 14px; }
        .small { font-size: 10px; }
        .divider { border-top: 1px dashed #000; margin: 8px 0; }
        .double-divider { border-top: 2px solid #000; margin: 8px 0; }
        .item-row { display: flex; justify-content: space-between; margin: 2px 0; }
        .item-name { flex: 1; }
        .item-qty { width: 30px; text-align: center; }
        .item-price { width: 50px; text-align: right; }
        .total-row { font-weight: bold; border-top: 1px solid #000; padding-top: 4px; margin-top: 8px; }
        .status { background: #000; color: #fff; padding: 2px 8px; font-size: 10px; }
      </style>
    `;

    const logoUrl = process.env.REACT_APP_MEDIA_URL
      ? `${process.env.REACT_APP_MEDIA_URL}/images/logo/logo.png`
      : "";

    const patientName = visit.patient
      ? `${visit.patient.firstname || ""} ${
          visit.patient.lastname || ""
        }`.trim()
      : visit.walkin_name || "Walk-in";

    const triageClass =
      visit.triage_level === 1
        ? "red"
        : visit.triage_level === 2
        ? "yellow"
        : "green";
    const triageText =
      visit.triage_level === 1
        ? "Red"
        : visit.triage_level === 2
        ? "Yellow"
        : "Green";

    const dispositionText = getDispositionText(visit.disposition);

    const chargesHtml =
      visit.charges && visit.charges.length > 0
        ? `
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Quantity</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          ${visit.charges
            .map(
              (charge) => `
            <tr>
              <td>${charge.item_name}</td>
              <td>${charge.quantity}</td>
                <td>${charge.unit_price} AFN</td>
                <td>${charge.total_price} AFN</td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>
    `
        : "";

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Emergency Visit Receipt - ${
          visit.receipt_number || visit.id
        }</title>
        ${styles}
      </head>
      <body>
        <div class="receipt">
          <div class="center large bold">${hospitalName}</div>
          ${
            hospitalAddress
              ? `<div class="center small">${hospitalAddress}</div>`
              : ""
          }
          ${
            hospitalPhone
              ? `<div class="center small">${phoneLabel}: ${hospitalPhone}</div>`
              : ""
          }
          <div class="divider"></div>
          
          <div class="center bold large">EMERGENCY RECEIPT</div>
          <div class="center small">Receipt #${
            visit.receipt_number || `ER-${visit.id}`
          }</div>
          <div class="center small">${new Date().toLocaleString()}</div>
          <div class="divider"></div>
          
          <div class="left">
            <div><strong>Patient:</strong> ${patientName}</div>
            <div><strong>Arrival:</strong> ${new Date(
              visit.arrival_time
            ).toLocaleString()}</div>
            <div><strong>Triage:</strong> ${triageText}</div>
            <div><strong>Disposition:</strong> ${dispositionText}</div>
            <div><strong>Payment:</strong> <span class="status">PAID</span> ${
              visit.payment_method || "Cash"
            }</div>
          </div>
          
          ${
            visit.chief_complaint
              ? `<div class="left small"><strong>Complaint:</strong> ${visit.chief_complaint}</div>`
              : ""
          }
          ${
            visit.diagnosis
              ? `<div class="left small"><strong>Diagnosis:</strong> ${visit.diagnosis}</div>`
              : ""
          }
          
          <div class="divider"></div>
          
          <div class="left bold">Services & Charges:</div>
          <div class="item-row">
            <span class="item-name">Emergency Visit Fee</span>
            <span class="item-qty">1</span>
            <span class="item-price">${visit.er_visit_fee || 0} AFN</span>
          </div>
          
          ${
            visit.charges && visit.charges.length > 0
              ? visit.charges
                  .map(
                    (charge) => `
            <div class="item-row">
              <span class="item-name">${charge.item_name}</span>
              <span class="item-qty">${charge.quantity}</span>
              <span class="item-price">${charge.total_price} AFN</span>
            </div>
          `
                  )
                  .join("")
              : ""
          }
          
          <div class="double-divider"></div>
          
          <div class="item-row total-row">
            <span class="item-name bold">TOTAL AMOUNT</span>
            <span class="item-price bold">${visit.total_amount || 0} AFN</span>
          </div>
          
          <div class="double-divider"></div>
          
          <div class="center small">
            <div>${t("print.thanks", { defaultValue: "Thank you for choosing" })}</div>
            <div class="bold">${hospitalName}</div>
            <div>${t("print.cosmos", { defaultValue: "Powered by Afghan Cosmos IT and Solution" })}</div>
            <div>Receipt generated: ${new Date().toLocaleString()}</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const getPaymentStatusChip = (status) => {
    switch (status) {
      case 1:
        return <Chip label="Pending" color="warning" size="small" />;
      case 2:
        return <Chip label="Paid" color="success" size="small" />;
      case 3:
        return <Chip label="Cancelled" color="error" size="small" />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  const getTriageChip = (level) => {
    switch (level) {
      case 1:
        return <Chip label="Red" color="error" size="small" />;
      case 2:
        return <Chip label="Yellow" color="warning" size="small" />;
      case 3:
        return <Chip label="Green" color="success" size="small" />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  const getDispositionText = (disposition) => {
    switch (disposition) {
      case 1:
        return "Discharge";
      case 2:
        return "Admit IPD";
      case 3:
        return "Admit ICU";
      case 4:
        return "Referral";
      case 5:
        return "Death";
      default:
        return "Unknown";
    }
  };

  const filteredVisits = visits.filter((visit) => {
    const searchLower = searchTerm.toLowerCase();
    const patientName = visit.patient
      ? `${visit.patient.firstname || ""} ${
          visit.patient.lastname || ""
        }`.toLowerCase()
      : (visit.walkin_name || "").toLowerCase();
    const receiptNumber = (visit.receipt_number || "").toLowerCase();

    return (
      patientName.includes(searchLower) || receiptNumber.includes(searchLower)
    );
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", backgroundColor: "#f5f5f5", p: 3 }}>
      <Paper sx={{ borderRadius: "15px", width: "100%", p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t("reception.emergencyFees", { defaultValue: "Emergency Fees" })}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Search by patient name or receipt number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Receipt #</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Arrival Time</TableCell>
              <TableCell>Triage</TableCell>
              <TableCell>Disposition</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVisits.map((visit) => (
              <TableRow key={visit.id}>
                <TableCell>
                  {visit.receipt_number || `ER-${visit.id}`}
                </TableCell>
                <TableCell>
                  {visit.patient
                    ? `${visit.patient.firstname || ""} ${
                        visit.patient.lastname || ""
                      }`.trim()
                    : visit.walkin_name || "Walk-in"}
                </TableCell>
                <TableCell>
                  {new Date(visit.arrival_time).toLocaleString()}
                </TableCell>
                <TableCell>{getTriageChip(visit.triage_level)}</TableCell>
                <TableCell>{getDispositionText(visit.disposition)}</TableCell>
                <TableCell>{visit.total_amount || 0} AFN</TableCell>
                <TableCell>
                  {getPaymentStatusChip(visit.payment_status)}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {visit.payment_status === 2 ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => printReceipt(visit)}
                      >
                        Print Receipt
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          setSelectedVisit(visit);
                          setPaymentDialog(true);
                        }}
                      >
                        Collect Payment
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {filteredVisits.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="textSecondary">
              No pending emergency payments found
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialog}
        onClose={() => setPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {selectedVisit && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Receipt:{" "}
                    {selectedVisit.receipt_number || `ER-${selectedVisit.id}`}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    Patient:{" "}
                    {selectedVisit.patient
                      ? `${selectedVisit.patient.firstname || ""} ${
                          selectedVisit.patient.lastname || ""
                        }`.trim()
                      : selectedVisit.walkin_name || "Walk-in"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" color="primary">
                    Total Amount: {selectedVisit.total_amount || 0} AFN
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <MenuItem value="Cash">Cash</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Payment Notes (Optional)"
                    multiline
                    rows={3}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Cancel</Button>
          <Button onClick={handlePayment} variant="contained" color="primary">
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="standard"
          sx={{
            minWidth: 280,
            bgcolor: "#fff",
            borderBottom:
              snackbar.severity === "success"
                ? "4px solid #37b24d"
                : "4px solid #d32f2f",
            boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
